// @story: libs/storybook/stories/components/input/recurrence-interval-input.stories.ts
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { RRuleFrequency } from '@zag/calendar/models';
import { ZagButton } from '../button/button';
import { NumberInput } from '../number-input/number-input';

export type RecurrenceFrequency = RRuleFrequency | 'quarterly';

export interface RecurrenceIntervalValue {
  frequency: RecurrenceFrequency;
  interval: number;
}

let nextRecurrenceIntervalId = 0;

@Component({
  selector: 'zag-recurrence-interval-input',
  imports: [TranslatePipe, ZagButton, NumberInput],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './recurrence-interval-input.component.scss',
  template: `
    <label class="zag-sr-only" [attr.for]="fieldId">{{ 'calendars.rrule.repeats.every' | translate }}</label>
    <zag-number-input
      data-testid="zag-recurrence-interval"
      class="zag-recurrence-interval-input__interval"
      [id]="fieldId"
      [value]="value()?.interval ?? null"
      (valueChange)="onIntervalChange($event)"
      [min]="min()"
      [max]="max()"
      [disabled]="disabled() || isFormDisabled()"
      [readonly]="readonly()"
      [clearable]="false"
    />
    <div
      class="zag-recurrence-interval-input__frequencies"
      role="radiogroup"
      [attr.aria-label]="'calendars.rrule.repeats.title' | translate"
    >
      @for (frequency of frequencies(); track frequency) {
        <button
          type="button"
          [class]="chipClass()"
          zagSize="sm"
          role="radio"
          [zagButton]="isSelected(frequency) ? 'basic' : 'outlined'"
          [zagType]="isSelected(frequency) ? 'primary' : 'secondary'"
          [attr.data-testid]="'zag-recurrence-frequency-' + frequency"
          [attr.aria-checked]="isSelected(frequency)"
          [disabled]="isLocked()"
          (click)="onFrequencyChange(frequency)"
        >
          {{ 'calendars.rrule.repeats.unit.' + frequency | translate }}
        </button>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RecurrenceIntervalInputComponent),
      multi: true,
    },
  ],
})
export class RecurrenceIntervalInputComponent implements ControlValueAccessor {
  readonly frequencies = input.required<RecurrenceFrequency[]>();
  readonly min = input(1);
  readonly max = input(99);
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly readonly = input(false, { transform: booleanAttribute });
  readonly chipClass = input<string>('');

  protected readonly fieldId = `zag-recurrence-interval-field-${nextRecurrenceIntervalId++}`;
  protected readonly value = signal<RecurrenceIntervalValue | null>(null);
  protected readonly isFormDisabled = signal(false);
  protected readonly isLocked = computed(() => this.disabled() || this.readonly() || this.isFormDisabled());

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: RecurrenceIntervalValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  protected isSelected(frequency: RecurrenceFrequency): boolean {
    return this.value()?.frequency === frequency;
  }

  protected onFrequencyChange(frequency: RecurrenceFrequency): void {
    if (this.isLocked()) return;
    this.emit({ frequency, interval: this.value()?.interval ?? this.min() });
  }

  protected onIntervalChange(interval: number | null): void {
    const current = this.value();
    if (this.isLocked() || !current) return;
    this.emit({ frequency: current.frequency, interval: interval ?? this.min() });
  }

  writeValue(value: RecurrenceIntervalValue | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: RecurrenceIntervalValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isFormDisabled.set(isDisabled);
  }

  private emit(value: RecurrenceIntervalValue): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }
}
